import { useEffect, useMemo, useState } from "react";
import { Drawer } from "@mui/material";
import { FileCode, FileText, History, Loader2, ShoppingCart, X } from "lucide-react";
import Aba from "../../boleto/remessa/_components/aba";
import Badge from "./badge";
import { AbaEventos, AbaResumo, AbaVenda, AbaXml } from "./painel-abas";
import { dataHora } from "./tabela-fiscal";
import { mensagemDeErro } from "../hooks";
import { PENDENCIAS } from "../pendencias";
import { ACOES, STATUS, TONS, descrever } from "../status";

const TOM_CONSULTA = { 100: "verde", 150: "verde", 101: "cinza", 151: "cinza", 135: "cinza", 155: "cinza" };

const ABAS = [
	{ chave: "resumo", texto: "Resumo", icone: <FileText size={16} /> },
	{ chave: "eventos", texto: "Eventos", icone: <History size={16} /> },
	{ chave: "xml", texto: "XML", icone: <FileCode size={16} /> },
	{ chave: "venda", texto: "Venda", icone: <ShoppingCart size={16} /> },
];

function useSobDemanda(carregar, ativa) {
	const [estado, setEstado] = useState(null);

	useEffect(() => {
		if (!ativa || estado) return;

		setEstado({ carregando: true });
		carregar()
			.then((dados) => setEstado({ dados }))
			.catch((erro) => setEstado({ erro: mensagemDeErro(erro) }));
	}, [ativa]);

	return estado;
}

function Conteudo({ item, aba, fonte, onAba, onFechar, onAcao }) {
	const documento = useMemo(() => fonte.documento(item), [item]);
	const base = { ...item, ...documento };
	const abaAtual = documento ? aba : "venda";

	const xml = useSobDemanda(() => fonte.xml(base), abaAtual === "xml");
	const venda = useSobDemanda(() => fonte.venda(base), abaAtual === "venda");

	const [executando, setExecutando] = useState(null);
	const [consulta, setConsulta] = useState(null);

	const pendencia = PENDENCIAS[item.tipo];
	const { acoes } = pendencia ?? STATUS[documento.status];
	const alvo = { ...base, ...xml?.dados };
	const abas = documento ? ABAS : ABAS.filter(({ chave }) => chave === "venda");

	async function acionar(acao) {
		setExecutando(acao);
		try {
			const retorno = await onAcao(acao, alvo);
			if (ACOES[acao].exibeRetorno && retorno) setConsulta(retorno);
		} finally {
			setExecutando(null);
		}
	}

	const conteudo = {
		resumo: () => <AbaResumo documento={documento} />,
		eventos: () => <AbaEventos documento={documento} />,
		xml: () => <AbaXml estado={xml} />,
		venda: () => <AbaVenda estado={venda} />,
	};

	return (
		<div className="flex flex-col h-full">
			<div className="shrink-0 border-b px-5 py-4 flex items-start justify-between gap-3">
				<div>
					<div className="flex items-center gap-2 flex-wrap">
						<h2 className="text-lg font-semibold text-gray-800 mb-0">{descrever(item)}</h2>
						{documento && <Badge {...STATUS[documento.status]} />}
					</div>
					<p className="text-sm text-gray-500 mb-0">
						{documento
							? `Série ${documento.serie} · ${dataHora(documento.data_emissao ?? documento.data_cadastro)}`
							: "Venda sem documento fiscal"}
					</p>
				</div>
				<button type="button" onClick={onFechar} className="p-1 rounded hover:bg-gray-100 text-gray-500">
					<X size={20} />
				</button>
			</div>

			{pendencia && (
				<div className={`shrink-0 mx-5 mt-4 rounded-md border px-3 py-2 text-sm ${TONS[pendencia.tom]}`}>
					<div className="text-xs font-semibold uppercase tracking-wide opacity-80">{pendencia.rotulo}</div>
					{item.motivo}
				</div>
			)}

			{acoes.length > 0 && (
				<div className="shrink-0 px-5 pt-4 flex flex-wrap gap-2">
					{acoes.map((acao) => {
						const { rotulo, icone, perigo } = ACOES[acao];
						const emAndamento = executando === acao;
						const Icone = emAndamento ? Loader2 : icone;

						return (
							<button
								key={acao}
								type="button"
								disabled={emAndamento}
								onClick={() => acionar(acao)}
								className={`btn btn-sm ${perigo ? "btn-outline-danger" : "btn-outline-primary"} inline-flex items-center gap-1.5`}
							>
								<Icone size={14} className={emAndamento ? "animate-spin" : ""} />
								{rotulo}
							</button>
						);
					})}
				</div>
			)}

			{consulta && (
				<div
					className={`shrink-0 mx-5 mt-4 rounded-md border px-3 py-2 text-sm ${TONS[TOM_CONSULTA[consulta.cstat] ?? "ambar"]}`}
				>
					<div className="text-xs font-semibold uppercase tracking-wide opacity-80">Consulta na SEFAZ</div>
					{[consulta.cstat, consulta.motivo].filter(Boolean).join(" · ")}
					{consulta.protocolo && (
						<div className="text-xs mt-0.5">
							Protocolo {consulta.protocolo} · {dataHora(consulta.data)}
						</div>
					)}
				</div>
			)}

			<div className="shrink-0 px-5 pt-4">
				<div className="inline-flex p-1 bg-gray-100/90 rounded-xl gap-1 border border-gray-200/60 shadow-inner">
					{abas.map(({ chave, texto, icone }) => (
						<Aba key={chave} ativa={abaAtual === chave} onClick={() => onAba(chave)} icone={icone} texto={texto} />
					))}
				</div>
			</div>

			<div className="flex-1 overflow-y-auto px-5 py-4">{conteudo[abaAtual]()}</div>
		</div>
	);
}

export default function PainelDocumento({ painel, fonte, onAba, onFechar, onAcao }) {
	return (
		<Drawer
			anchor="right"
			open={Boolean(painel)}
			onClose={onFechar}
			disableEnforceFocus
			PaperProps={{ sx: { width: { xs: "100%", sm: 600 } } }}
		>
			{painel && <Conteudo {...painel} fonte={fonte} onAba={onAba} onFechar={onFechar} onAcao={onAcao} />}
		</Drawer>
	);
}
